import { useCallback, useEffect, useRef, useState } from "react";
import { get } from "../api.js";
import { diaryPaymentClassifications, type DiaryPaymentClassification } from "./diaryPayments.js";

type Window = { from: string; to: string };
interface ReadScope {
  key: string;
  enabled: boolean;
  generation: number;
  payments: Map<number, DiaryPaymentClassification>;
  versions: Map<number, number>;
  inFlight: Map<number, number>;
  windowPending: boolean;
}
const unknownPayment = (appointment_id: number): DiaryPaymentClassification => ({
  appointment_id, method: "unknown", status: "unknown", payment_id: null, amount_due: null,
});

/** Live Diary state. Visible ids are used only for gaps after the window read. */
export function useDiaryPayments(
  window: Window,
  tutorsKey: string,
  enabled: boolean,
  visibleIds: readonly number[] = [],
) {
  const generation = useRef(0);
  const key = JSON.stringify([window.from, window.to, tutorsKey, enabled]);
  const makeScope = (): ReadScope => ({
    key, enabled: enabled && tutorsKey !== "", generation: ++generation.current, payments: new Map(), versions: new Map(),
    inFlight: new Map(), windowPending: enabled && tutorsKey !== "",
  });
  const [scope, setScope] = useState(makeScope);
  const [revision, setRevision] = useState(0);
  // Reset during render: neither an old marker nor its response survives even
  // the first committed render of a new window/tutor selection.
  let current = scope;
  if (scope.key !== key) {
    current = makeScope();
    setScope(current);
  }
  const active = useRef(current);
  active.current = current;
  const notify = () => setRevision((value) => value + 1);

  useEffect(() => {
    let mounted = true;
    if (!scope.enabled) return;
    const requestGeneration = scope.generation;
    void get<DiaryPaymentClassification[]>("/admin/bookings/payment-classifications", {
      from: window.from, to: window.to,
      ...(tutorsKey === "all" ? {} : { tutors: tutorsKey }),
    }).then((rows) => {
      if (!mounted || active.current !== scope) return;
      const next = new Map(scope.payments);
      for (const row of rows) {
        // A write invalidates only its ids; unaffected window results may land.
        if ((scope.versions.get(row.appointment_id) ?? 0) <= requestGeneration) {
          next.set(row.appointment_id, row);
        }
      }
      scope.payments = next;
    }).catch(() => {
      // No assertion of payment state on failure. Visible gaps can still use
      // the established id endpoint once this window attempt has finished.
    }).finally(() => {
      if (!mounted || active.current !== scope) return;
      scope.windowPending = false;
      notify();
    });
    return () => { mounted = false; };
  }, [scope]);

  const refreshAll = useCallback(() => {
    const previous = active.current;
    const next: ReadScope = {
      ...previous, generation: ++generation.current, payments: new Map(), versions: new Map(),
      inFlight: new Map(), windowPending: previous.enabled,
    };
    active.current = next;
    setScope(next);
  }, []);

  const refreshOne = useCallback(async (id: number) => {
    const target = active.current;
    const requestGeneration = ++generation.current;
    target.versions.set(id, requestGeneration);
    target.inFlight.set(id, requestGeneration);
    target.payments = new Map(target.payments);
    target.payments.delete(id);
    notify();
    if (!target.enabled) return;
    let rows: DiaryPaymentClassification[] = [];
    try { rows = await diaryPaymentClassifications([id]); } catch { /* fail closed */ }
    if (active.current !== target || target.versions.get(id) !== requestGeneration) return;
    target.inFlight.delete(id);
    target.payments = new Map(target.payments);
    target.payments.set(id, rows.find((row) => row.appointment_id === id) ?? unknownPayment(id));
    notify();
  }, []);

  const visibleKey = [...new Set(visibleIds.filter((id) => Number.isInteger(id) && id > 0))].sort((a, b) => a - b).join(",");
  useEffect(() => {
    if (!scope.enabled || scope.windowPending || active.current !== scope || !visibleKey) return;
    const ids = visibleKey.split(",").map(Number).filter((id) =>
      !scope.payments.has(id) && !scope.inFlight.has(id),
    );
    if (!ids.length) return;
    const requestGeneration = generation.current;
    for (const id of ids) scope.inFlight.set(id, requestGeneration);
    void diaryPaymentClassifications(ids).catch(() => []).then((rows) => {
      if (active.current !== scope) return;
      const byId = new Map(rows.map((row) => [row.appointment_id, row]));
      const next = new Map(scope.payments);
      for (const id of ids) {
        if (scope.inFlight.get(id) !== requestGeneration ||
          (scope.versions.get(id) ?? 0) > requestGeneration) continue;
        scope.inFlight.delete(id);
        next.set(id, byId.get(id) ?? unknownPayment(id));
      }
      scope.payments = next;
      notify();
    });
  }, [scope, visibleKey, revision]);

  return { payments: current.payments as ReadonlyMap<number, DiaryPaymentClassification>, refreshAll, refreshOne };
}
